import { ForwardedRef, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

import DecoratedInputField from "./DecoratedInputField";
import { IInputFieldWithValidationProps } from "components/InputFieldWithValidation";

import { cx } from "utils";


const InputFieldComponents = forwardRef((
  {
    label,
    labelPosition = "top",
    isValidated = false,
    requiredErrorMessage = "This field is required.",
    fullWidth = false,
    typeMismatchErrorMessage = "Please enter a valid value.",
    tooLongErrorMessage = "Value is too long.",
    tooShortErrorMessage = "Value is too short.",
    patternMismatchErrorMessage = "Value is not properly formatted.",
    TrailingElement,
    inputPaddingForTrailingElement = "pr-10",
    errorMessage,
    successMessage,
    formatValue,
    unformatValue,
    unformattedInputRef,
    relative,
    onFocusLost,
    resetKey,
    ...props
  }: IInputFieldWithValidationProps,
  ref: ForwardedRef<HTMLInputElement | null>
) => {
  const [ validationErrorMessage, setValidationErrorMessage ] = useState<string | JSX.Element>(<>&nbsp;</>);
  const [ showValidationError, setShowValidationError ] = useState(false);
  const [ initialTrackedValue ] = useMemo(
    () => {
      const initialValue = props.value ?? props.defaultValue;
      return initialValue && formatValue ? formatValue(initialValue.toString()) : [ initialValue || "" ];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ props.value, props.defaultValue ]
  );
  const [ trackedValue, setTrackedValue ] = useState(initialTrackedValue);
  const [ cursorPosition, setCursorPosition ] = useState<number | null>();

  const setProperErrorMessage = useCallback((htmlInput: HTMLInputElement) => {
    const validityState = htmlInput.validity;
    if (validityState.customError) {
      return setValidationErrorMessage(htmlInput.validationMessage);
    }
    if (validityState.typeMismatch) {
      return setValidationErrorMessage(typeMismatchErrorMessage);
    }
    if (validityState.valueMissing) {
      return setValidationErrorMessage(requiredErrorMessage);
    }
    if (validityState.tooLong) {
      return setValidationErrorMessage(tooLongErrorMessage);
    }
    if (validityState.tooShort) {
      return setValidationErrorMessage(tooShortErrorMessage);
    }
    if (validityState.patternMismatch) {
      return setValidationErrorMessage(patternMismatchErrorMessage);
    }

    setValidationErrorMessage(<>&nbsp;</>);
  }, [ setValidationErrorMessage, typeMismatchErrorMessage, requiredErrorMessage, tooLongErrorMessage, tooShortErrorMessage, patternMismatchErrorMessage ]);

  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => inputRef.current!, []);

  useEffect(() => {
    if (inputRef.current) {
      if (errorMessage !== undefined) {
        inputRef.current.setCustomValidity(errorMessage);
        setValidationErrorMessage(errorMessage);
      } else if (successMessage !== undefined) {
        inputRef.current.setCustomValidity("");
        setValidationErrorMessage("");
      } else {
        if (
          props.required
          && typeof trackedValue === "string"
          && trackedValue.length > 0
          && trackedValue.trim() === ""
        ) {
          inputRef.current.setCustomValidity(requiredErrorMessage);
        } else {
          inputRef.current.setCustomValidity("");
        }
        setProperErrorMessage(inputRef.current);
      }
      inputRef.current.form?.dispatchEvent(new Event("input", { bubbles: true }));
    }
  }, [ isValidated, errorMessage, successMessage, setProperErrorMessage, trackedValue, props.type, props.pattern, props.minLength, props.maxLength, props.required, requiredErrorMessage ]);  // also revalidate if any of the validation properties changed

  useEffect(() => {
    if (cursorPosition && inputRef.current) {
      inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
    }
  }, [ trackedValue, cursorPosition, inputRef ]);

  useEffect(() => {
    setTrackedValue(initialTrackedValue);
  }, [ initialTrackedValue ])

  useEffect(() => {
    if (resetKey !== undefined && resetKey > 0) {
      setTrackedValue(initialTrackedValue);
      setShowValidationError(false);
      if (unformattedInputRef?.current) {
        unformattedInputRef.current.value = "";
      }
    }
  }, [ resetKey, initialTrackedValue, unformattedInputRef ])

  return (
    <>
      <div
        className={ cx(
          "group gap-y-1",
          labelPosition === "top" && "flex flex-col",
          labelPosition === "left" && "grid grid-cols-[auto,auto] gap-x-2 items-center justify-start",
          labelPosition === "right" && "grid grid-cols-[auto,auto] gap-x-2 items-center justify-start",
          labelPosition === "bottom" && "flex flex-col"
        ) }
      >
        <label
          className={ cx(
            "flex flex-col gap-y-1 text-sm text-gray dark:text-neutral-600 select-none",
            (props.type === "checkbox") && (labelPosition === "left" || labelPosition === "right") && "grid grid-cols-subgrid col-span-2 items-center justify-start"
          ) }
        >
          <div className={ cx((labelPosition === "right" || labelPosition === "bottom") && "order-2") }>
            { label }
          </div>

          <DecoratedInputField
            { ...props }
            ref={ inputRef }
            value={ trackedValue }
            className={ cx(
              props.className,
              (showValidationError || isValidated) && "invalid:text-red dark:invalid:text-error-600 placeholder:invalid:text-red-400 dark:placeholder:invalid:text-error-400 invalid:border-red dark:invalid:border-error-600 focus:invalid:ring-red dark:focus:invalid:ring-error-600",
              relative && "relative"
            ) }
            TrailingElement={ TrailingElement }
            inputPaddingForTrailingElement={ inputPaddingForTrailingElement }

            onChange={ (event: any) => {
              props.onChange?.(event);

              if (formatValue && unformatValue) {
                const [ unformattedValue, cursorPosition ] = unformatValue(event.currentTarget.value, event.currentTarget.selectionStart || 0);
                if (unformattedInputRef?.current && unformattedValue) {
                  unformattedInputRef.current.value = unformattedValue;
                }
                const [ formattedValue, newCursorPosition ] = formatValue(unformattedValue, cursorPosition);

                setTrackedValue(formattedValue);
                setCursorPosition(newCursorPosition);
              } else {
                setTrackedValue(event.currentTarget.value);
                setCursorPosition(event.currentTarget.selectionStart);
              }

              setShowValidationError(true);
              props.onChange?.(event);  // TODO check if we need to call it again... it's been called above at the start of the event handler
              setProperErrorMessage(event.currentTarget);
            } }
            onBlur={ (event: any) => {
              setShowValidationError(true);
              props.onBlur?.(event);
              onFocusLost?.(event, setTrackedValue);
              setProperErrorMessage(event.currentTarget);
            } }
          />
        </label>

        <div className={ cx(
          "text-sm",
          validationErrorMessage && "text-red dark:text-error-600 opacity-0",
          successMessage && "text-gray dark:text-neutral-600",
          (showValidationError || isValidated) && "group-has-[:invalid]:opacity-100",
          labelPosition === "right" && "order-3",
          labelPosition === "bottom" && "order-3"
        ) }
        >
          { validationErrorMessage || successMessage }
        </div>
      </div>
    </>
  );
});


export default InputFieldComponents;
